// Cliente HTTP del backend. Cuanto sale a la red pasa por aqui, sin excepcion.
//
// Existe para que ninguna pantalla vea un `fetch`, un codigo de estado ni una forma
// de error del servidor. Cuando eso se reparte, cada pantalla inventa su propio
// manejo y los tres casos que de verdad importan —422 con faltantes, 409 de
// duplicado, 409 de version— acaban tratados como "error generico".
//
// DE DONDE SALE LA BASE DE LAS URL.
//
// En desarrollo las rutas son relativas a `/api` y las resuelve el proxy de Vite.
// En despliegue NO: se antepone la URL absoluta de la API del ambiente, que Vite
// congela en el bundle al construir desde `VITE_API_URL`.
//
// Aqui decia que "en despliegue lo enruta Static Web Apps". ERA FALSO, y costo
// caro: enrutar `/api` a un Container App exige el plan Standard de Static Web
// Apps —el nuestro es Free— y ademas Azure proxea la ruta ENTERA, prefijo `/api`
// incluido, contra una API que sirve en la raiz. Sin plan y sin prefijo, `/api`
// caia en el 404 en HTML del propio sitio estatico, el cliente intentaba leerlo
// como JSON y la pantalla mostraba "No se pudo cargar la lista de paises".
//
// Se noto recien cuando los paises dejaron de estar en duro en el front: mientras
// vivieron en el bundle, nadie necesitaba que el navegador alcanzara la API.
//
// El pipeline YA construye una vez por ambiente y YA inyecta `VITE_API_URL`
// (azure-pipelines-app.yml); lo que faltaba era que alguien la leyera.
//
// El respaldo a `/api` NO es un adorno: es lo que mantiene vivo el proxy de Vite
// en local y lo que hace que las pruebas -donde la variable no existe- sigan
// comprobando rutas relativas.

import { esUuid } from '../lib/identificadores';
import { modoDeIdentidad, tokenDeAcceso } from '../lib/identidad';

// Las barras del final se quitan CONTANDO, no con `/\/+$/`. La expresion era
// correcta pero de coste cuadratico ante una entrada patologica —"////…////x" obliga
// al motor a reintentar desde cada barra (S8786)—, y la variable la escribe una
// persona en un variable group. Recortar hacia atras es lineal y se lee igual.
import type { Faltante } from '../tipos';

/**
 * El cuerpo de una respuesta de error. Es RFC 9457 con los nombres en espanol del
 * backend (`titulo`, `detalle`, `codigo`), no los del ingles: buscar `detail` devuelve
 * siempre undefined salvo en los errores de Pydantic, que si usan el suyo.
 */
export type CuerpoDeError = {
  codigo?: string;
  titulo?: string;
  detalle?: string;
  detail?: unknown;
  faltantes?: Faltante[];
  candidatos?: unknown[];
  errores?: { campo: string; detalle: string }[];
  /** CC-01: el campo del formulario al que se refiere un 422 de regla (`texto`, `motivo`, `archivo`). */
  campo?: string;
} | null;

/**
 * Un fallo ya listo para pintarse en pantalla.
 *
 * Existe porque un `catch` recibe `unknown` -en JavaScript se puede lanzar cualquier
 * cosa- y las pantallas leen siempre lo mismo: el mensaje, y el estado para distinguir
 * "no hay red" (0) de "el servidor dijo que no". Normalizarlo en un solo sitio evita
 * que cada pantalla afirme por su cuenta que lo capturado era un Error.
 */
export type Fallo = {
  message: string;
  /** El codigo HTTP. 0 = la peticion no llego a salir. Solo lo traen los del API. */
  estado?: number;
  /**
   * El cuerpo problem+json, cuando lo hubo. Las pantallas lo consultan para distinguir
   * fallos que exigen otra reaccion: `custodia_rota` (ADR-003) no es pasajero y
   * reintentar no lo arregla, asi que se avisa en vez de ofrecer reintentar.
   */
  cuerpo?: CuerpoDeError;
  /**
   * Lo que el servidor dijo de verdad cuando `message` es la frase generica de
   * infraestructura. Las pantallas lo pintan plegado en un `<details>`: sirve a
   * soporte, no al usuario.
   */
  detalleTecnico?: string;
};

/**
 * MENSAJE PARA EL USUARIO ANTE UN FALLO DE INFRAESTRUCTURA (14-sep-2026).
 *
 * Un 500 con «rol de base fv_lectura no concedido» o un 403 de PostgreSQL no le dice
 * nada a un registrador, y lo que le decia era peor que nada: mandaba a buscar el
 * problema en el formulario. Se sustituye por una frase que dice QUE hacer —avisar a
 * soporte— y CON QUE —el codigo de la instancia—, y el texto original se conserva en
 * `detalleTecnico`, plegado, para quien lo entienda.
 */
export const MENSAJE_DE_INFRAESTRUCTURA = 'No se pudo completar la operación por un problema de configuración del sistema.';

// Lo que delata un 403 de INFRAESTRUCTURA y no de autorizacion de la aplicacion: el 403
// por rol («No tienes acceso a esta operación...», sin codigos de rol desde el BUG-1098)
// es un mensaje de negocio y se queda; el «rol de base ... no concedido», un archivo .sql
// o un modulo .py son plomeria y se traducen.
const HUELLA_DE_INFRAESTRUCTURA = /RolDeBaseNoConcedido|rol(?:es)? de (?:la )?base|\.sql\b|\.py\b|postgres|permission denied|traceback/i;

const esFalloDeInfraestructura = (estado: number | undefined, mensaje: string): boolean => {
  if (estado === undefined || estado === 0) return false;
  if (estado >= 500) return true;
  return estado === 403 && HUELLA_DE_INFRAESTRUCTURA.test(mensaje);
};

/** El identificador con el que soporte encuentra la traza: `instancia` del problem+json o un id. */
const codigoDeSoporte = (cuerpo: CuerpoDeError): string | null => {
  const c = cuerpo as (Record<string, unknown> | null);
  const candidato = c?.instancia ?? c?.instance ?? c?.id ?? c?.trace_id ?? c?.correlation_id;
  return typeof candidato === 'string' && candidato ? candidato : null;
};

/** Normaliza cualquier cosa lanzada a un `Fallo` que se pueda mostrar. */
export function aFallo(e: unknown): Fallo {
  if (e instanceof ErrorDeApi) {
    if (esFalloDeInfraestructura(e.estado, e.message)) {
      const codigo = codigoDeSoporte(e.cuerpo);
      return {
        message: `${MENSAJE_DE_INFRAESTRUCTURA} Avisa a soporte${codigo ? ` con el código ${codigo}` : ''}.`,
        estado: e.estado,
        cuerpo: e.cuerpo,
        detalleTecnico: e.message,
      };
    }
    return { message: e.message, estado: e.estado, cuerpo: e.cuerpo };
  }
  if (e instanceof Error) return { message: e.message };
  return { message: String(e ?? 'Ocurrió un error inesperado.') };
}

export type OpcionesDePeticion = {
  metodo?: string;
  cuerpo?: unknown;
  senal?: AbortSignal;
};

const sinBarrasFinales = (url: string): string => {
  let fin = url.length;
  while (fin > 0 && url[fin - 1] === '/') fin -= 1;
  return url.slice(0, fin);
};

// La base de las URL NO se exporta (BUG-1090): un enlace o un `window.open` hacia la API sale sin
// el token de `cabecerasDe` y con Entra recibe 401. Cada petición a la API pasa por `pedir` o
// `pedirBinario`; un documento para otra pestaña, por `lib/abrirDocumento.ts`.
const BASE = sinBarrasFinales(import.meta.env.VITE_API_URL || '/api');

// Codifica un segmento de ruta antes de interpolarlo en la URL.
//
// Los identificadores llegan del servidor o de la barra de direcciones, y un `..`,
// un `/` o un `?` dentro de uno cambiaria el endpoint al que apunta la peticion:
// `/casos/${'..%2fadmin'}` no consulta un caso, consulta otra cosa. El backend exige
// autenticacion, asi que el alcance es limitado — pero limitar el alcance de un fallo
// no es lo mismo que no tenerlo, y aqui la correccion cuesta una llamada.
//
// Se aplica en el punto de construccion, no dentro de `pedir()`: `pedir` recibe la
// ruta ya armada y no puede distinguir un separador legitimo de uno inyectado.
// Una barra, un punto doble o un signo de interrogacion NO son parte de un
// identificador: son un intento de cambiar el endpoint al que apunta la peticion.
//
// ESTE es el alfabeto COMUN, y desde T-45 se reserva para lo que de verdad no tiene
// una forma mas estrecha: los codigos de catalogo (`categoria_ea`) y los booleanos.
// El caso, el documento y el destinatario van por `segUuid`, y el pais por
// `segPais`, porque de esos el backend SI declara el formato exacto y aceptar menos
// es cerrar mas.
const SEGMENTO_VALIDO = /^[A-Za-z0-9._-]+$/;

// El pais del correlativo (RN-07). Misma regla que el `pattern` de la ruta del
// backend: dos letras mayusculas, nada mas.
const PAIS_ISO2 = /^[A-Z]{2}$/;

/**
 * Valida y codifica un segmento de ruta.
 *
 * Codificar SOLO ya cerraba el agujero -`..%2fadmin` no navega a ningun sitio-, pero
 * dejaba pasar en silencio un identificador corrupto hasta que el servidor respondia
 * 404, y ahi el mensaje ya no dice cual era el problema. Rechazarlo aqui falla en el
 * sitio donde se puede entender.
 *
 * Se conserva la codificacion aunque la validacion ya garantice que sobra: si manana
 * el alfabeto se amplia, el orden correcto sigue siendo validar y despues codificar.
 */
export function seg(valor: unknown): string {
  const texto = String(valor);
  if (!SEGMENTO_VALIDO.test(texto)) {
    throw new ErrorDeApi(`Identificador no válido para una ruta: ${JSON.stringify(texto)}`, {
      estado: 0,
      cuerpo: null,
    });
  }
  return encodeURIComponent(texto);
}

/**
 * Como `seg`, pero para los identificadores que el backend declara UUID.
 *
 * POR QUE NO BASTABA `seg` (T-45, jssecurity:S8476). Su alfabeto es el maximo comun
 * de lo que la API acepta en una ruta —uuid, codigo de catalogo, booleano—, asi que
 * `casos-de-otro` pasaba por identificador de caso valido. Pero el caso, el
 * documento y el destinatario NO son cualquier texto: el backend los tipa como
 * `UUID` en la firma de la ruta, y el del caso ademas puede venir de FUERA de la
 * aplicacion —el enlace profundo del correo de HU-10 lo lee de la barra de
 * direcciones—, que es la definicion de dato contaminado.
 *
 * Comprobar el formato EXACTO antes de componer la URL cierra el unico hueco que
 * quedaba y ademas falla donde se entiende: sin esto, un identificador corrupto se
 * descubria como un 404 o un 422 del servidor, y ese mensaje ya no dice cual era la
 * causa. La codificacion se conserva aunque el formato ya garantice que sobra: el
 * orden correcto es validar y despues codificar, y asi sigue valiendo el dia que
 * alguien amplie lo que se acepta.
 *
 * Es `DatosInvalidos` —y no `ErrorDeApi` a secas como `seg`— porque eso es
 * exactamente lo que ocurrio: la aplicacion iba a mandar un dato que el servidor
 * rechazaria. Las pantallas ya saben mostrar ese error.
 */
export function segUuid(valor: unknown): string {
  const texto = String(valor);
  if (!esUuid(texto)) {
    throw new DatosInvalidos({
      detalle: `Identificador no válido para una ruta: ${JSON.stringify(texto)} no es un UUID`,
    });
  }
  return encodeURIComponent(texto);
}

/**
 * El codigo ISO-2 del pais, para `GET /casos/series/{pais}`.
 *
 * Es el otro valor que llega a una URL desde la eleccion del usuario (el selector de
 * pais del formulario, y el `pais` del usuario guardado en `sessionStorage`). El
 * backend lo declara `pattern=r"^[A-Z]{2}$"`; esta es la misma regla comprobada
 * antes de salir, para que un valor manipulado no llegue a componer la peticion.
 */
export function segPais(valor: unknown): string {
  const texto = String(valor);
  if (!PAIS_ISO2.test(texto)) {
    throw new DatosInvalidos({
      detalle: `Identificador no válido para una ruta: ${JSON.stringify(texto)} no es un código ISO-2 de país`,
    });
  }
  return encodeURIComponent(texto);
}

/** Error con cuerpo: conserva el estado y lo que respondio el servidor. */
export class ErrorDeApi extends Error {
  /** El codigo HTTP. 0 significa que la peticion no llego a salir (sin red). */
  readonly estado: number;
  readonly cuerpo: CuerpoDeError;

  constructor(mensaje: string, { estado, cuerpo }: { estado: number; cuerpo: CuerpoDeError }) {
    super(mensaje);
    this.name = 'ErrorDeApi';
    this.estado = estado;
    this.cuerpo = cuerpo;
  }
}

/**
 * El caso no esta completo para enviarse (HU-01, HU-08).
 *
 * `faltantes` llega del dominio con campo, seccion y detalle de CADA dato que falta.
 * HU-01 es explicita: "me dice cuantos DATOS faltan, no cuantas secciones" y "los
 * lista todos". Se conserva la lista entera, no el primero.
 */
export class CasoIncompleto extends ErrorDeApi {
  readonly faltantes: Faltante[];

  constructor(cuerpo: CuerpoDeError) {
    super('El caso todavía no se puede enviar.', { estado: 422, cuerpo });
    this.name = 'CasoIncompleto';
    this.faltantes = cuerpo?.faltantes ?? [];
  }
}

/** HU-07: el envio encontro un caso parecido y hay que preguntar antes de seguir. */
export class PosibleDuplicado extends ErrorDeApi {
  readonly candidatos: unknown[];

  constructor(cuerpo: CuerpoDeError) {
    super('Este caso se parece a otro ya registrado.', { estado: 409, cuerpo });
    this.name = 'PosibleDuplicado';
    this.candidatos = cuerpo?.candidatos ?? [];
  }
}

/**
 * HU-36: el caso se guardo desde otro sitio antes. No se pisa; se avisa.
 *
 * El mensaje NO dice "alguien mas": culpar a un tercero manda a buscar el problema
 * fuera, y la causa mas frecuente es la propia pestana (otra ventana del mismo
 * usuario, o un guardado que la pantalla no contabilizo). Lo unico que el cliente
 * sabe de verdad es que la version cambio.
 */
export class ConflictoDeVersion extends ErrorDeApi {
  constructor(cuerpo: CuerpoDeError) {
    super('Este borrador cambió desde que lo abriste; tus cambios no se guardaron.', {
      estado: 409,
      cuerpo,
    });
    this.name = 'ConflictoDeVersion';
  }
}

/**
 * 409 `transicion_no_permitida`: una REGLA DE NEGOCIO, no un conflicto de version.
 *
 * «Completa el Formato Amarillo antes de preparar la carta» y «La carta no está firmada»
 * llegan con este codigo. Clasificarlos como `ConflictoDeVersion` los escondia tras
 * «este borrador cambió desde que lo abriste», que es falso y manda a recargar algo que
 * no cambio. El mensaje del servidor es la frase util, y es la que se muestra.
 */
export class ReglaDeNegocio extends ErrorDeApi {
  constructor(cuerpo: CuerpoDeError) {
    super(mensajeDe(cuerpo) || 'La operación incumple una regla de negocio.', { estado: 409, cuerpo });
    this.name = 'ReglaDeNegocio';
  }
}

// La lista campo a campo que arma Pydantic cuando la peticion no pasa la frontera.
// Va aparte de la del dominio (`errores`) porque son dos formas distintas del mismo
// cuerpo y anidar las dos comprobaciones en una sola expresion las hacia ilegibles.
const detallesDePydantic = (cuerpo: CuerpoDeError): unknown[] => (Array.isArray(cuerpo?.detail) ? cuerpo.detail : []);

/**
 * LOS 422 DE ESQUEMA SE TRADUCEN (QA, 14-sep-2026). Pydantic redacta en inglés y por
 * tipo —«String should match pattern '^[^@]+@[^@]+$'»— y eso llegaba crudo al usuario.
 * Aquí se pasa a una frase por campo en español; lo que no se reconoce conserva el
 * detalle original, que sigue siendo mejor que nada.
 */
export const MENSAJE_CORREO_INVALIDO = 'Escribe un correo válido (nombre@dominio)';

const POR_TIPO: Record<string, string> = {
  missing: 'Este dato es obligatorio',
  string_too_short: 'Es demasiado corto',
  string_too_long: 'Es demasiado largo',
  string_pattern_mismatch: 'El formato no es válido',
  extra_forbidden: 'No se esperaba este dato',
  int_parsing: 'Debe ser un número entero',
  int_type: 'Debe ser un número entero',
  float_parsing: 'Debe ser un número',
  float_type: 'Debe ser un número',
  bool_parsing: 'Debe ser sí o no',
  date_parsing: 'La fecha no es válida (AAAA-MM-DD)',
  date_from_datetime_parsing: 'La fecha no es válida (AAAA-MM-DD)',
  datetime_parsing: 'La fecha y hora no son válidas',
  uuid_parsing: 'El identificador no es válido',
  enum: 'El valor no está entre los permitidos',
  literal_error: 'El valor no está entre los permitidos',
  greater_than_equal: 'Está por debajo del mínimo permitido',
  greater_than: 'Está por debajo del mínimo permitido',
  less_than_equal: 'Supera el máximo permitido',
  less_than: 'Supera el máximo permitido',
  too_short: 'Faltan elementos',
  too_long: 'Sobran elementos',
  json_invalid: 'El contenido no se pudo leer',
};

/** Una entrada de la lista campo a campo del servidor (`errores`) o de Pydantic (`detail`). */
type ErrorDeCampo = { campo?: string; detalle?: string; tipo?: string; loc?: unknown[]; msg?: string; type?: string };

/** «body.notificante.correo» → «notificante.correo»: el prefijo del cuerpo no le dice nada a nadie. */
const nombreDelCampo = (campo: string): string => campo.replace(/^(body|query|path)\./, '');

export function traducirErrorDeCampo(e: ErrorDeCampo): { campo: string; detalle: string } {
  const campo = nombreDelCampo(e.campo ?? (Array.isArray(e.loc) ? e.loc.map(String).join('.') : ''));
  const tipo = e.tipo ?? e.type ?? '';
  const original = e.detalle ?? e.msg ?? '';
  if (/correo|email/i.test(campo) && tipo !== 'missing') return { campo, detalle: MENSAJE_CORREO_INVALIDO };
  const traducido = POR_TIPO[tipo];
  return { campo, detalle: traducido ?? original };
}

/** Datos rechazados en la frontera (Pydantic) o por una regla de negocio. */
export class DatosInvalidos extends ErrorDeApi {
  readonly detalles: unknown[];

  constructor(cuerpo: CuerpoDeError) {
    // EL MENSAJE DEL SERVIDOR VA PRIMERO. El backend se molesta en redactar QUE
    // esta mal — "No pueden presentar signos 11 animales si solo se trataron 5",
    // "Algun codigo de la peticion no esta en su catalogo", o la lista campo a
    // campo de pydantic — y aqui se aplanaba todo al generico "hay datos que el
    // servidor no acepta": el registrador acababa adivinando con las herramientas
    // de red del navegador (asi se depuro la jornada entera del 27-ago-2026). Orden: la lista
    // campo-a-campo si existe (esquema), luego `detalle` (reglas de negocio y
    // catalogos, que ahi traen la frase util), luego `titulo`, y el generico solo
    // si el cuerpo no trajo nada.
    const errores = Array.isArray(cuerpo?.errores) ? cuerpo.errores : [];
    const traducidos = (errores.length ? errores : detallesDePydantic(cuerpo)).map((e) => traducirErrorDeCampo(e as ErrorDeCampo));
    const porCampo = traducidos
      .slice(0, 3)
      .map((e) => `${e.campo}: ${e.detalle}`)
      .join(' · ');
    super(
      porCampo || cuerpo?.detalle || cuerpo?.titulo || 'Hay datos que el sistema no acepta.',
      { estado: 422, cuerpo },
    );
    this.name = 'DatosInvalidos';
    this.detalles = traducidos;
  }
}

// ⚠ Los errores NO llegan como `application/json`, sino como
// **`application/problem+json`** (RFC 9457), y `'application/problem+json'
// .includes('application/json')` es **false**: `application/json` no es subcadena de
// `application/problem+json`.
//
// Comprobarlo mal costaba caro y en silencio: se descartaba el cuerpo de TODOS los
// errores, asi que la lista de faltantes de HU-01 y los candidatos a duplicado de
// HU-07 se perdian y el usuario veia "el servidor respondio con un error" en vez de
// lo que le falta.
const ES_JSON = /json/i;

async function leerCuerpo(respuesta: Response): Promise<CuerpoDeError> {
  const tipo = respuesta.headers.get('content-type') || '';
  if (!ES_JSON.test(tipo)) return null;
  try {
    return await respuesta.json();
  } catch {
    return null;
  }
}

// Un mismo estado significa cosas distintas: dos 422 y dos 409 que piden acciones
// opuestas del usuario. Se distinguen por el campo `codigo`, que el backend declara
// "identificador estable y legible por maquina" — y no por adivinar la forma del
// cuerpo, que nadie promete mantener.
//
// La forma queda como respaldo: si un manejador nuevo olvidara el codigo, el error
// se sigue clasificando bien en vez de caer al generico.
const CODIGO = {
  CASO_INCOMPLETO: 'caso_incompleto',
  POSIBLES_DUPLICADOS: 'posibles_duplicados',
  CONFLICTO_DE_CONCURRENCIA: 'conflicto_de_concurrencia',
  TRANSICION_NO_PERMITIDA: 'transicion_no_permitida',
  // Sprint 3 (Administración): el correo ya dado de alta (HU-32b) y la etiqueta repetida de un
  // catálogo (HU-30-C2). Son reglas con el texto del criterio en `detalle`; sin esta línea caían
  // en el respaldo de abajo y la pantalla decía «Este borrador cambió…», que no tiene nada que ver.
  USUARIO_YA_REGISTRADO: 'usuario_ya_registrado',
  ETIQUETA_DUPLICADA: 'etiqueta_duplicada',
  // Dos personas publicaron a la vez la misma versión de una plantilla de carta. El `detalle`
  // manda a recargar para ver la vigente; «Este borrador cambió…» no tiene nada que ver.
  PLANTILLA_PUBLICADA_A_LA_VEZ: 'plantilla_publicada_a_la_vez',
};

function errorDe409(cuerpo: CuerpoDeError): ErrorDeApi {
  if (cuerpo?.codigo === CODIGO.POSIBLES_DUPLICADOS) return new PosibleDuplicado(cuerpo);
  if (cuerpo?.codigo === CODIGO.CONFLICTO_DE_CONCURRENCIA) return new ConflictoDeVersion(cuerpo);
  if (cuerpo?.codigo === CODIGO.TRANSICION_NO_PERMITIDA) return new ReglaDeNegocio(cuerpo);
  if (cuerpo?.codigo === CODIGO.USUARIO_YA_REGISTRADO) return new ReglaDeNegocio(cuerpo);
  if (cuerpo?.codigo === CODIGO.ETIQUETA_DUPLICADA) return new ReglaDeNegocio(cuerpo);
  if (cuerpo?.codigo === CODIGO.PLANTILLA_PUBLICADA_A_LA_VEZ) return new ReglaDeNegocio(cuerpo);
  if (Array.isArray(cuerpo?.candidatos)) return new PosibleDuplicado(cuerpo);
  return new ConflictoDeVersion(cuerpo);
}

// El del dominio trae `faltantes`; el de Pydantic, `detail` con los campos que no
// pasaron la validacion de la frontera.
function errorDe422(cuerpo: CuerpoDeError): ErrorDeApi {
  if (cuerpo?.codigo === CODIGO.CASO_INCOMPLETO) return new CasoIncompleto(cuerpo);
  if (Array.isArray(cuerpo?.faltantes)) return new CasoIncompleto(cuerpo);
  return new DatosInvalidos(cuerpo);
}

// El envoltorio de error es RFC 9457 con los nombres en espanol del backend:
// `titulo`, `detalle`, `codigo`. Buscar `detail` —el nombre en ingles— devolvia
// siempre undefined y el usuario acababa leyendo el mensaje generico en vez del que
// el servidor se molesto en redactar.
function mensajeDe(cuerpo: CuerpoDeError): string | null {
  if (cuerpo?.detalle) return cuerpo.detalle;
  if (cuerpo?.titulo) return cuerpo.titulo;
  // `detail` es el nombre de Pydantic y ahi suele venir la LISTA campo a campo, no
  // una frase. Solo sirve como mensaje cuando de verdad es texto; si es el array, lo
  // que se muestra al usuario lo arma `DatosInvalidos`, que sabe recorrerlo.
  return typeof cuerpo?.detail === 'string' ? cuerpo.detail : null;
}

// ---------------------------------------------------------------------------------
// LA SESIÓN EN CADA PETICIÓN (HU-32a, MG-ILE-63 · tarea 32a-5)
//
// `Authorization: Bearer` se pone AQUÍ y en ningún otro sitio (AT S3 §1.2). En modo simulado
// `tokenDeAcceso()` devuelve `null` y la petición sale como hasta hoy. En modo Entra, sin
// token (Microsoft pidió volver a ingresar) la petición no sale: es un 401 local, igual que
// el del servidor, y los dos avisan a quien escucha la sesión.
//
// Quien escucha es la raíz de la aplicación: un 401 abre «Tu sesión terminó. Vuelve a
// ingresar.» (HU-32a-C5) y un 403 le hace volver a preguntar `GET /sesion`, porque un usuario
// desactivado o sin roles recibe 403 en su siguiente petición (HU-32b-C4) y tiene que ver
// «Sin acceso», no el «No tienes acceso a esta opción» de una pantalla suelta.
// ---------------------------------------------------------------------------------

/** Lo que el cliente le cuenta a la raíz: la sesión caducó (401) o algo se denegó (403). */
export type EventoDeSesion = 'caducada' | 'denegada';
type OyenteDeSesion = (evento: EventoDeSesion) => void;
const oyentesDeSesion = new Set<OyenteDeSesion>();

/** Suscribe a los 401 y 403 de cualquier petición. Devuelve la baja. */
export function escucharLaSesion(oyente: OyenteDeSesion): () => void {
  oyentesDeSesion.add(oyente);
  return () => { oyentesDeSesion.delete(oyente); };
}

function avisarALaSesion(estado: number): void {
  const evento: EventoDeSesion | null = estado === 401 ? 'caducada' : estado === 403 ? 'denegada' : null;
  if (evento) oyentesDeSesion.forEach((oyente) => oyente(evento));
}

/** El texto del 401 local (sin token): el mismo aviso que abre la raíz. */
export const MENSAJE_DE_SESION_CADUCADA = 'Tu sesión terminó. Vuelve a ingresar.';

/** Las cabeceras de una petición: el tipo del cuerpo y, con Entra, el token. */
async function cabecerasDe(conCuerpo: boolean): Promise<Record<string, string>> {
  const cabeceras: Record<string, string> = conCuerpo ? { 'Content-Type': 'application/json' } : {};
  if (modoDeIdentidad() !== 'entra') return cabeceras;
  const token = await tokenDeAcceso();
  if (!token) {
    avisarALaSesion(401);
    throw new ErrorDeApi(MENSAJE_DE_SESION_CADUCADA, { estado: 401, cuerpo: null });
  }
  return { ...cabeceras, Authorization: `Bearer ${token}` };
}

export async function pedir<T = unknown>(ruta: string, { metodo = 'GET', cuerpo, senal }: OpcionesDePeticion = {}): Promise<T> {
  const headers = await cabecerasDe(cuerpo !== undefined);
  let respuesta: Response;
  try {
    respuesta = await fetch(`${BASE}${ruta}`, {
      method: metodo,
      headers,
      body: cuerpo === undefined ? undefined : JSON.stringify(cuerpo),
      signal: senal,
    });
  } catch (error) {
    // Se distingue de un error del servidor a proposito: "no hay red" y "el servidor
    // dijo que no" piden acciones distintas del usuario.
    if (error instanceof Error && error.name === 'AbortError') throw error;
    throw new ErrorDeApi('No se pudo contactar con el servidor.', {
      estado: 0,
      cuerpo: null,
    });
  }

  if (respuesta.ok) {
    return (respuesta.status === 204 ? null : await leerCuerpo(respuesta)) as T;
  }

  throw await errorDeRespuesta(respuesta);
}

/**
 * Traduce una respuesta NO-ok al error tipado que le corresponde.
 *
 * Extraida de `pedir` cuando aparecio `pedirBinario` (T-38): los errores de la ruta
 * binaria llegan igual en problem+json, y mantener dos escaleras de estados es la
 * receta para que un dia clasifiquen distinto el mismo 409.
 */
async function errorDeRespuesta(respuesta: Response): Promise<ErrorDeApi> {
  const cuerpoDeError = await leerCuerpo(respuesta);
  avisarALaSesion(respuesta.status);
  if (respuesta.status === 422) return errorDe422(cuerpoDeError);
  if (respuesta.status === 409) return errorDe409(cuerpoDeError);
  if (respuesta.status === 404) {
    return new ErrorDeApi(mensajeDe(cuerpoDeError) || 'No se encontró lo que se pidió.', {
      estado: 404,
      cuerpo: cuerpoDeError,
    });
  }
  if (respuesta.status === 503) {
    // Despliegue incompleto, no dato del usuario: falta reponer una clave de
    // configuracion. Mandarlo a revisar el formulario seria mandarlo a arreglar algo
    // que esta bien. El `detalle` del backend nombra la variable que falta.
    return new ErrorDeApi(
      mensajeDe(cuerpoDeError) || 'El sistema no está completamente configurado.',
      { estado: 503, cuerpo: cuerpoDeError },
    );
  }
  if (respuesta.status === 429) {
    return new ErrorDeApi('Demasiadas peticiones seguidas. Espera unos segundos.', {
      estado: 429,
      cuerpo: cuerpoDeError,
    });
  }
  return new ErrorDeApi(mensajeDe(cuerpoDeError) || 'El sistema respondió con un error.', {
    estado: respuesta.status,
    cuerpo: cuerpoDeError,
  });
}

/**
 * Como `pedir`, pero para respuestas BINARIAS (el PDF de un documento, T-38).
 *
 * Existe porque `pedir` interpreta el cuerpo como JSON —`leerCuerpo` devuelve `null`
 * ante `application/pdf`— y un visor que recibe `null` no tiene nada que mostrar.
 * Comparte base, manejo de red y clasificacion de errores: los fallos de la ruta
 * binaria SI llegan como problem+json (404 `recurso_no_encontrado`, 403
 * `acceso_denegado` para documentos con dato L2 —esa descarga va por la via
 * auditada de HU-33—, 500 `custodia_rota` si la huella no cuadra, ADR-003).
 *
 * Devuelve el `Blob` crudo: la URL de objeto la crea —y la REVOCA— quien lo
 * muestra, porque su ciclo de vida es el del visor, no el de la peticion.
 */
export async function pedirBinario(ruta: string, { senal }: { senal?: AbortSignal } = {}): Promise<Blob> {
  const headers = await cabecerasDe(false);
  let respuesta: Response;
  try {
    respuesta = await fetch(`${BASE}${ruta}`, { headers, signal: senal });
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw error;
    throw new ErrorDeApi('No se pudo contactar con el servidor.', {
      estado: 0,
      cuerpo: null,
    });
  }

  if (!respuesta.ok) throw await errorDeRespuesta(respuesta);
  return respuesta.blob();
}
